import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import { speakText, stopSpeech, PRONUNCIATION_PASS_SCORE } from "../../utils/audioHelpers";
import PronunciationPractice from "../common/PronunciationPractice";
import "../../assets/css/storytelling.css";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
function getLangTag(langId) {
    return LANG_TAG[parseInt(langId, 10)] ?? "de-DE";
}

function pronunciationWordClassName(word) {
    if (word.error_type === "Omission") {
        return "pronunciation-word pronunciation-word--omission";
    }
    if (word.accuracy_score >= 80) {
        return "pronunciation-word pronunciation-word--good";
    }
    if (word.accuracy_score >= 50) {
        return "pronunciation-word pronunciation-word--ok";
    }
    return "pronunciation-word pronunciation-word--poor";
}

function VolumeIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
    );
}

function StopIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
        </svg>
    );
}

function ArrowRightIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
        </svg>
    );
}

function ArrowLeftIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
        </svg>
    );
}

function CheckmarkIcon({ size = 12 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function CrossIcon({ size = 12 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function QuestionBox({ question, storyId, onSolved, langId }) {
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [status, setStatus] = useState(null); // 'checking', 'correct', 'incorrect'
    const [correctIdx, setCorrectIdx] = useState(null);
    const [shuffledOptions, setShuffledOptions] = useState([]);

    useEffect(() => {
        if (question && question.options) {
            const mapped = question.options.map((opt, idx) => ({ originalIndex: idx, optionText: opt }));
            for (let i = mapped.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [mapped[i], mapped[j]] = [mapped[j], mapped[i]];
            }
            setShuffledOptions(mapped);
        } else {
            setShuffledOptions([]);
        }
    }, [question]);

    const handleOptionSelect = (shuffledIdx, originalIdx) => {
        if (status === "correct") return;
        setSelectedIdx(shuffledIdx);
        setStatus("checking");

        fetch(`${API_BASE_URL}/api/story/check-answer`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                story_id: storyId,
                question_id: question.id,
                selected_index: originalIdx
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.correct) {
                    setStatus("correct");
                    setTimeout(() => {
                        onSolved();
                    }, 1000);
                } else {
                    setStatus("incorrect");
                    setCorrectIdx(data.correct_index);
                }
            })
            .catch(err => {
                console.error(err);
                setStatus(null);
            });
    };

    const [pronunciationAttempt, setPronunciationAttempt] = useState(0);
    const [pronunciationResult, setPronunciationResult] = useState(null);

    useEffect(() => {
        setPronunciationAttempt(0);
        setPronunciationResult(null);
        setStatus(null);
        setSelectedIdx(null);
        setCorrectIdx(null);
    }, [question.id]);

    const handlePronunciationResult = (result) => {
        const isCorrect = result.overall_score >= PRONUNCIATION_PASS_SCORE;
        setPronunciationResult(result);
        setStatus(isCorrect ? "correct" : "incorrect");
    };

    const handleRedo = () => {
        setStatus(null);
        setPronunciationResult(null);
        setPronunciationAttempt(prev => prev + 1);
    };

    if (question.type === "pronounce") {
        return (
            <div className="question-box">
                <span className="question-box__badge">Pronunciation</span>
                <p className="question-box__prompt">{question.prompt}</p>
                <div style={{ marginTop: 16 }}>
                    {status === null ? (
                        <PronunciationPractice
                            key={`${question.id}-${pronunciationAttempt}`}
                            text={question.text_to_pronounce}
                            langCode={getLangTag(langId)}
                            onResult={handlePronunciationResult}
                        />
                    ) : (
                        <div className="test-pronunciation-feedback" style={{ marginTop: 16 }}>
                            <div className={`test-feedback ${status === "correct" ? "test-feedback--correct" : "test-feedback--wrong"}`}>
                                {status === "correct" ? "Excellent!" : "Keep practicing!"}
                            </div>
                            <div className="pronunciation-overall-label">Overall pronunciation score</div>
                            <div className="pronunciation-overall-score">
                                {Math.round(pronunciationResult?.overall_score || 0)}
                            </div>

                            <div className="test-pronunciation-word-list" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', margin: '16px 0' }}>
                                {pronunciationResult?.words?.map((word, index) => (
                                    <div key={`${word.word}-${index}`} className="test-pronunciation-word-item">
                                        <span
                                            className={pronunciationWordClassName(word)}
                                            onClick={() => speakText(word.word, getLangTag(langId))}
                                            title="Click to hear pronunciation"
                                        >
                                            {word.word}
                                        </span>
                                        <span className="test-pronunciation-word-score">
                                            {word.error_type === "Omission"
                                                ? "Missed"
                                                : `${Math.round(word.accuracy_score)}%`}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="test-feedback-actions" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                                <button type="button" className="test-redo-btn" onClick={handleRedo}>
                                    Try again
                                </button>
                                {status === "correct" && (
                                    <button type="button" className="test-submit-btn" onClick={onSolved}>
                                        Next
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="question-box">
            <span className="question-box__badge">{question.type}</span>
            <p className="question-box__prompt">{question.prompt}</p>
            <div className="options-list">
                {shuffledOptions.map((item, idx) => {
                    let className = "option-btn";
                    if (selectedIdx === idx) {
                        if (status === "correct") className += " option-btn--correct";
                        else if (status === "incorrect") className += " option-btn--incorrect";
                        else className += " option-btn--selected";
                    } else if (status === "incorrect" && correctIdx === item.originalIndex) {
                        className += " option-btn--correct";
                    }

                    return (
                        <button
                            key={idx}
                            className={className}
                            onClick={() => handleOptionSelect(idx, item.originalIndex)}
                            disabled={status === "checking" || status === "correct"}
                        >
                            <span>{item.optionText}</span>
                            {selectedIdx === idx && status === "correct" && (
                                <span className="option-indicator option-indicator--correct">
                                    <CheckmarkIcon size={12} />
                                </span>
                            )}
                            {selectedIdx === idx && status === "incorrect" && (
                                <span className="option-indicator option-indicator--incorrect">
                                    <CrossIcon size={12} />
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function InteractiveText({ text, langTag, speakTextFn }) {
    const [activeTranslation, setActiveTranslation] = useState(null);

    // Close translation tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setActiveTranslation(null);
        window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, []);

    // Split text into paragraphs
    const paragraphs = text.split("\n\n");

    const handleSentenceClick = (plainText) => {
        speakTextFn(plainText, langTag);
    };

    return (
        <>
            {paragraphs.map((p, pIdx) => {
                // Split paragraph into sentences. 
                // Regex matches any characters up to a punctuation mark (. ! ? …), including the punctuation mark, optional quotes/dashes, and trailing spaces.
                const sentences = p.match(/[^.?!…]+[.?!…]+["”'»›«“„’‘—–-]*(?:\s+|$)|[^.?!…]+$/g) || [p];

                return (
                    <p key={pIdx}>
                        {sentences.map((sentence, sIdx) => {
                            // Extract plain text for TTS (remove [[word|translation]] tags)
                            const plainText = sentence.replace(/\[\[(.*?)\|(.*?)\]\]/g, "$1");

                            // Parse sentence to render tagged words
                            const parts = [];
                            const regex = /\[\[(.*?)\|(.*?)\]\]/g;
                            let lastIndex = 0;
                            let match;

                            while ((match = regex.exec(sentence)) !== null) {
                                if (match.index > lastIndex) {
                                    parts.push(sentence.substring(lastIndex, match.index));
                                }
                                const word = match[1];
                                const translation = match[2];
                                parts.push(
                                    <span
                                        key={match.index}
                                        className="tagged-word"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveTranslation({ word, translation, x: e.clientX, y: e.clientY });
                                        }}
                                    >
                                        {word}
                                    </span>
                                );
                                lastIndex = regex.lastIndex;
                            }
                            if (lastIndex < sentence.length) {
                                parts.push(sentence.substring(lastIndex));
                            }

                            return (
                                <span
                                    key={sIdx}
                                    className="interactive-sentence"
                                    onClick={() => handleSentenceClick(plainText)}
                                    title="Click to hear pronunciation"
                                >
                                    {parts.map((part, i) => <React.Fragment key={i}>{part}</React.Fragment>)}
                                </span>
                            );
                        })}
                    </p>
                );
            })}

            {activeTranslation && (
                <div
                    className="translation-tooltip"
                    style={{
                        left: activeTranslation.x,
                        top: activeTranslation.y + 15
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="translation-tooltip__word">{activeTranslation.word}</div>
                    <div className="translation-tooltip__trans">{activeTranslation.translation}</div>
                </div>
            )}
        </>
    );
}

function StoryReader({ story, langId, enablePronunciation, onComplete }) {
    const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [readAll, setReadAll] = useState(false);

    // Track solved inline questions
    const [solvedInlineQuestions, setSolvedInlineQuestions] = useState({});
    const [activeInlineQuestion, setActiveInlineQuestion] = useState(null);

    // Stop speaking if segment or readAll changes
    useEffect(() => {
        stopSpeech();
        setIsPlaying(false);
    }, [currentSegmentIndex, readAll]);

    // Cleanup speech on unmount
    useEffect(() => {
        return () => stopSpeech();
    }, []);

    const speakTextContent = (text, customLangTag) => {
        if (isPlaying) {
            stopSpeech();
            setIsPlaying(false);
        } else {
            setIsPlaying(true);
            speakText(text, customLangTag || getLangTag(langId), {
                rate: 0.85,
                onEnd: () => setIsPlaying(false),
                onError: () => setIsPlaying(false)
            });
        }
    };

    const speakCurrentSegment = (text) => {
        // Strip out any translation tags before reading full page
        const plainText = text.replace(/\[\[(.*?)\|(.*?)\]\]/g, "$1");
        speakTextContent(plainText);
    };

    const inlineQuestionForCurrentSegment = story.questions.find(
        q => q.display_after_segment === currentSegmentIndex && (enablePronunciation || q.type !== "pronounce")
    );

    const handleNext = () => {
        // Check if there is an inline question for this segment
        if (inlineQuestionForCurrentSegment && !solvedInlineQuestions[inlineQuestionForCurrentSegment.id] && !activeInlineQuestion) {
            // Show inline question first
            setActiveInlineQuestion(inlineQuestionForCurrentSegment);
            return;
        }

        if (currentSegmentIndex < story.segments.length - 1) {
            setCurrentSegmentIndex(prev => prev + 1);
            setActiveInlineQuestion(null);
        } else {
            onComplete();
        }
    };

    const handlePrev = () => {
        if (activeInlineQuestion) {
            setActiveInlineQuestion(null);
            return;
        }
        if (currentSegmentIndex > 0) {
            setCurrentSegmentIndex(prev => prev - 1);
            setActiveInlineQuestion(null);
        }
    };

    const handleInlineSolved = (qId) => {
        setSolvedInlineQuestions(prev => ({ ...prev, [qId]: true }));
        setActiveInlineQuestion(null);

        // Move to next segment automatically after a small delay
        if (currentSegmentIndex < story.segments.length - 1) {
            setCurrentSegmentIndex(prev => prev + 1);
        } else {
            onComplete();
        }
    };

    if (readAll) {
        return (
            <div className="story-segment">
                <div className="audio-action-row">
                    <button
                        className={`tts-btn ${isPlaying ? "tts-btn--playing" : ""}`}
                        onClick={() => speakCurrentSegment(story.segments.map(s => s.text).join("\n\n"))}
                    >
                        {isPlaying ? <StopIcon size={14} /> : <VolumeIcon size={14} />}
                        <span>{isPlaying ? "Stop Listening" : "Listen to Story"}</span>
                    </button>
                </div>

                <div className="story-segment__text">
                    <InteractiveText
                        text={story.segments.map(s => s.text).join("\n\n")}
                        langTag={getLangTag(langId)}
                        speakTextFn={speakTextContent}
                    />
                </div>

                <div className="story-nav-actions">
                    <label className="read-all-toggle">
                        <input
                            type="checkbox"
                            checked={readAll}
                            onChange={(e) => setReadAll(e.target.checked)}
                        />
                        <span>Show all pages</span>
                    </label>

                    <button className="story-nav-btn story-nav-btn--next" onClick={onComplete}>
                        <span>Take Quiz</span>
                        <ArrowRightIcon size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="story-segment">
            <div className="audio-action-row">
                <button
                    className={`tts-btn ${isPlaying ? "tts-btn--playing" : ""}`}
                    disabled={!!activeInlineQuestion}
                    onClick={() => speakCurrentSegment(story.segments[currentSegmentIndex].text)}
                >
                    {isPlaying ? <StopIcon size={14} /> : <VolumeIcon size={14} />}
                    <span>{isPlaying ? "Stop Listening" : "Listen Page"}</span>
                </button>
            </div>

            {!activeInlineQuestion ? (
                <div className="story-segment__text">
                    <InteractiveText
                        text={story.segments[currentSegmentIndex].text}
                        langTag={getLangTag(langId)}
                        speakTextFn={speakTextContent}
                    />
                </div>
            ) : (
                <QuestionBox
                    question={activeInlineQuestion}
                    storyId={story.id}
                    langId={langId}
                    onSolved={() => handleInlineSolved(activeInlineQuestion.id)}
                />
            )}

            <div className="story-nav-actions">
                <button
                    className="story-nav-btn story-nav-btn--prev"
                    onClick={handlePrev}
                    disabled={currentSegmentIndex === 0 && !activeInlineQuestion}
                    style={{ opacity: (currentSegmentIndex === 0 && !activeInlineQuestion) ? 0.3 : 1 }}
                >
                    <ArrowLeftIcon size={16} />
                    <span>Back</span>
                </button>

                <label className="read-all-toggle">
                    <input
                        type="checkbox"
                        checked={readAll}
                        onChange={(e) => setReadAll(e.target.checked)}
                    />
                    <span>Show all pages</span>
                </label>

                {!activeInlineQuestion && (
                    <button className="story-nav-btn story-nav-btn--next" onClick={handleNext}>
                        <span>
                            {currentSegmentIndex === story.segments.length - 1 && (!inlineQuestionForCurrentSegment || solvedInlineQuestions[inlineQuestionForCurrentSegment.id])
                                ? "Take Quiz"
                                : "Next"}
                        </span>
                        <ArrowRightIcon size={16} />
                    </button>
                )}
            </div>
        </div>
    );
}

export default StoryReader;
